import * as React from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fa } from '@/i18n/fa';

export const ToastProvider = ToastPrimitive.Provider;

export function ToastViewport({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitive.Viewport>) {
  return (
    <ToastPrimitive.Viewport
      className={cn(
        'fixed bottom-0 z-[100] flex max-h-screen w-full flex-col gap-2 p-4 pb-safe sm:bottom-4 sm:end-4 sm:w-96',
        className,
      )}
      {...props}
    />
  );
}

const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center justify-between gap-3 overflow-hidden rounded-card border p-4 shadow-raised data-[state=open]:animate-slide-up',
  {
    variants: {
      variant: {
        default: 'border-border bg-surface text-text',
        success: 'border-success/30 bg-surface text-text',
        destructive: 'border-danger/30 bg-surface text-text',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export function Toast({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof ToastPrimitive.Root> & VariantProps<typeof toastVariants>) {
  return <ToastPrimitive.Root className={cn(toastVariants({ variant, className }))} {...props} />;
}

export function ToastTitle({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitive.Title>) {
  return <ToastPrimitive.Title className={cn('text-sm font-bold', className)} {...props} />;
}

export function ToastDescription({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitive.Description>) {
  return (
    <ToastPrimitive.Description className={cn('text-sm text-text-muted', className)} {...props} />
  );
}

export function ToastClose({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitive.Close>) {
  return (
    <ToastPrimitive.Close
      aria-label={fa.common.close}
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-full text-text-subtle transition-colors hover:bg-surface-raised hover:text-text',
        className,
      )}
      {...props}
    >
      <X className="size-4" />
      <span className="sr-only">{fa.common.close}</span>
    </ToastPrimitive.Close>
  );
}

export type ToastActionElement = React.ReactElement<typeof ToastPrimitive.Action>;
